/* eslint-disable jsx-a11y/no-autofocus */
/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import { PrimitiveRecord } from '@smartsheet-dataflow/formjs';
import { EntityId } from '@smartsheet-dataflow/formjs-react';
import {
  Button,
  colors,
  FormLabel,
  sizes,
  Spacer,
  TextInput,
  TypeRampV2,
} from '@smartsheet/lodestar-core';
import { RemoveIcon, ToolbarChevronUpIcon } from '@smartsheet/lodestar-icons';
import React, { useState } from 'react';
import useCollapse from 'react-collapsed';

interface InputMapProps extends EntityId {
  label: string;
  value: PrimitiveRecord[];
  onChange: (newValue: PrimitiveRecord[]) => void;
  isOpen: boolean;
}

export const InputMap = ({
  id,
  label,
  value = [],
  onChange,
  isOpen,
}: InputMapProps) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(isOpen);
  const [isKeyFocused, setKeyFocus] = React.useState(true);
  const { getToggleProps, getCollapseProps } = useCollapse({
    isExpanded,
  });

  const style = css`
    box-sizing: border-box;
    cursor: pointer;
    display: flex;
    :hover,
    :focus {
      div {
        background-color: ${colors.focusLight20};
      }
    }
  `;

  const labelStyle = css`
    line-height: ${sizes.xLarge}px !important;
    width: calc(100% - ${sizes.xLarge}px);
  `;

  const chevronContainerStyle = css`
    border-radius: 2px;
    box-sizing: border-box;
    height: ${sizes.xLarge}px;
    width: ${sizes.xLarge}px;
    padding: calc(11px / 2);
  `;

  const chevronStyle = css`
    transition: transform 200ms;
    ${isExpanded ? 'transform: rotate(180deg)' : 'transform: rotate(90deg)'}
  `;

  const onKeyChange = (index: number, newValue: string) => {
    const recordValue = value[index] ? value[index].value : '';
    setKeyFocus(true);
    onChange([
      ...value.slice(0, index),
      { key: newValue, value: recordValue },
      ...value.slice(index + 1),
    ]);
  };

  const onValueChange = (index: number, newValue: string) => {
    const key = value[index] ? value[index].key : '';
    setKeyFocus(false);
    onChange([
      ...value.slice(0, index),
      { key, value: newValue },
      ...value.slice(index + 1),
    ]);
  };

  const onClick = (index: number) => () => {
    onChange([...value.slice(0, index), ...value.slice(index + 1)]);
  };

  return (
    <React.Fragment>
      <div
        css={style}
        {...getToggleProps({
          onClick: () => {
            setIsExpanded(!isExpanded);
          },
        })}
        id={id}
      >
        <TypeRampV2 system="lodestar" variant={'headerSmall'} css={labelStyle}>
          {label}
        </TypeRampV2>
        <div css={chevronContainerStyle}>
          <ToolbarChevronUpIcon css={chevronStyle} />
        </div>
      </div>
      <Spacer orientation="column" space="large" {...getCollapseProps()}>
        {Array.isArray(value) &&
          value.map((record, index: number) => (
            <Spacer key={index} orientation="row" space="small">
              <FormLabel label={`Key #${index + 1}`}>
                {inputProps => (
                  <TextInput
                    {...inputProps}
                    value={record.key}
                    onChange={value => onKeyChange(index, value)}
                    autoFocus={isKeyFocused}
                  />
                )}
              </FormLabel>
              <FormLabel label={`Value #${index + 1}`}>
                {inputProps => (
                  <TextInput
                    {...inputProps}
                    value={record.value as string}
                    onChange={value => onValueChange(index, value)}
                    autoFocus={!isKeyFocused}
                  />
                )}
              </FormLabel>
              <Button
                appearance="secondary-destructive"
                css={css`
                  margin-top: ${sizes.large + 1}px !important;
                `}
                iconBefore={<RemoveIcon />}
                onClick={onClick(index)}
              />
            </Spacer>
          ))}
        <Spacer key={`#${value.length}`} orientation="row" space="small">
          <FormLabel label={`Key #${value.length + 1}`}>
            {inputProps => (
              <TextInput
                {...inputProps}
                value={''}
                onChange={newValue => onKeyChange(value.length, newValue)}
              />
            )}
          </FormLabel>
          <FormLabel label={`Value #${value.length + 1}`}>
            {inputProps => (
              <TextInput
                {...inputProps}
                value={''}
                onChange={newValue => onValueChange(value.length, newValue)}
              />
            )}
          </FormLabel>
        </Spacer>
      </Spacer>
    </React.Fragment>
  );
};
